import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { useNotification } from '../../context/NotificationContext';
import Navigation from '../Home/Navigation';
import CaptchaBox from './CaptchaBox';
import styles from './LoginPage.module.css';

const LoginPage: React.FC = () => {
  const { t } = useTranslation('login');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const { showNotification } = useNotification();
  
  // Check URL params for initial tab
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get('tab') === 'signup' ? 'signup' : 'login';
  
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(initialTab);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUnverifiedError, setIsUnverifiedError] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  
  // Animation states - simplified
  const [currentFeature, setCurrentFeature] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  
  // Update tab when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get('tab') === 'signup' ? 'signup' : 'login';
    setActiveTab(tabFromUrl);
    setError(null);
    setSuccessMessage(null);
  }, [location.search]);
  
  // Form data
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    videoType: '',
    videoTypeOther: '',
    recaptchaToken: null as string | null
  });

  // Get redirect path from location state or default to '/home' (main dashboard)
  const from = (location.state as any)?.from?.pathname || '/home';

  // Subtle feature rotation - less frequent
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 5000); // Every 5 seconds instead of 2.5
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsUnverifiedError(false);
    setIsLoading(true);

    try {
      await login(loginData.email, loginData.password);
      
      showNotification({
        title: t('login.welcomeBackNotification'),
        message: t('login.loginSuccessMessage'),
        type: 'success'
      });
      
      // ProtectedRoute will handle the redirect based on user plan
      // Users with no_plan -> /pricing
      // Users with a plan -> /home (or intended destination)
    } catch (error: any) {
      const errorMessage = error.message || t('login.loginFailed');
      setError(errorMessage);
      
      // Check if error is due to unverified email
      if (errorMessage.toLowerCase().includes('verify') || 
          errorMessage.toLowerCase().includes('verification')) {
        setIsUnverifiedError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!loginData.email) {
      setError(t('login.emailRequired'));
      return;
    }

    setIsResendingVerification(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/resend-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: loginData.email }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(t('login.verificationEmailResent', 'Verification email sent! Please check your inbox.'));
        setIsUnverifiedError(false);
        showNotification({
          title: t('login.emailSent'),
          message: t('login.verificationEmailResent', 'Verification email sent! Please check your inbox.'),
          type: 'success',
          duration: 8000
        });
      } else {
        setError(data.message || t('login.resendFailed', 'Failed to resend verification email'));
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setError(t('login.resendFailed', 'Failed to resend verification email'));
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    // Check if running on localhost
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Validation
    if (signupData.password !== signupData.confirmPassword) {
      setError(t('login.passwordMismatch'));
      setIsLoading(false);
      return;
    }

    if (signupData.password.length < 6) {
      setError(t('login.passwordTooShort'));
      setIsLoading(false);
      return;
    }

    if (!signupData.videoType) {
      setError(t('login.videoTypeRequired'));
      setIsLoading(false);
      return;
    }

    if (signupData.videoType === 'other' && !signupData.videoTypeOther.trim()) {
      setError(t('login.videoTypeOtherRequired'));
      setIsLoading(false);
      return;
    }

    // Skip reCAPTCHA validation on localhost
    if (!isLocalhost && !signupData.recaptchaToken) {
      setError(t('login.captchaRequired'));
      setIsLoading(false);
      return;
    }

    try {
      await register(
        signupData.email, 
        signupData.password,
        signupData.videoType,
        signupData.videoType === 'other' ? signupData.videoTypeOther : undefined,
        signupData.recaptchaToken || undefined
      );
      setSuccessMessage(t('login.checkEmailVerification'));
      showNotification({
        title: t('login.emailSent'),
        message: t('login.checkEmailMessage'),
        type: 'info',
        duration: 10000
      });
      // Switch to login tab
      setActiveTab('login');
    } catch (error: any) {
      setError(error.message || t('login.registrationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    setSignupData(prev => ({ ...prev, recaptchaToken: token }));
  };

  const benefits = [
    {
      icon: '✨',
      title: t('login.benefitAITitle'),
      description: t('login.benefitAIDescription')
    },
    {
      icon: '⚡',
      title: t('login.benefitStudioTitle'),
      description: t('login.benefitStudioDescription')
    },
    {
      icon: '🎯',
      title: t('login.benefitQualityTitle'),
      description: t('login.benefitQualityDescription')
    },
    {
      icon: '💰',
      title: t('login.benefitSavingsTitle'),
      description: t('login.benefitSavingsDescription')
    }
  ];

  const features = [
    { icon: '🎯', title: t('login.featurePromptsTitle'), description: t('login.featurePromptsDescription') },
    { icon: '🎵', title: t('login.featureAudioTitle'), description: t('login.featureAudioDescription') },
    { icon: '📝', title: t('login.featureCaptionsTitle'), description: t('login.featureCaptionsDescription') },
    { icon: '✍️', title: t('login.featureTitlesTitle'), description: t('login.featureTitlesDescription') },
    { icon: '🖼️', title: t('login.featureThumbnailsTitle'), description: t('login.featureThumbnailsDescription') },
    { icon: '📤', title: t('login.featureShareTitle'), description: t('login.featureShareDescription') }
  ];

  return (
    <div className={styles.container}>
      <Navigation />
      
      <main className={styles.main}>
        {/* Left Section - Authentication Forms */}
        <div className={styles.leftSection}>
          <div className={styles.authContainer}>
            <div className={styles.authHeader}>
              <h1 className={styles.authTitle}>
                {activeTab === 'login' ? t('login.welcomeBack') : t('login.joinTitle')}
              </h1>
              <p className={styles.authSubtitle}>
                {activeTab === 'login' 
                  ? t('login.welcomeSubtitle')
                  : t('login.joinSubtitle')
                }
              </p>
            </div>

            <div className={styles.tabNavigation}>
              <button
                className={`${styles.tabButton} ${activeTab === 'login' ? styles.active : ''}`}
                onClick={() => {
                  setActiveTab('login');
                  setError(null);
                  setSuccessMessage(null);
                }}
              >
                {t('login.signIn')}
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'signup' ? styles.active : ''}`}
                onClick={() => {
                  setActiveTab('signup');
                  setError(null);
                  setSuccessMessage(null);
                }}
              >
                {t('login.signUp')}
              </button>
            </div>

            {activeTab === 'login' ? (
              <form className={styles.authForm} onSubmit={handleLogin}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('login.email')}</label>
                  <input
                    type="email"
                    className={styles.formInput}
                    placeholder={t('login.emailPlaceholder')}
                    value={loginData.email}
                    onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('login.password')}</label>
                  <div className={styles.passwordContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className={styles.formInput}
                      placeholder={t('login.passwordPlaceholder')}
                      value={loginData.password}
                      onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                      required
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                <div className={styles.forgotPasswordContainer}>
                  <Link to="/forgot-password" className={styles.forgotPasswordLink}>
                    {t('login.forgotPassword')}
                  </Link>
                </div>

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isLoading}
                >
                  {isLoading ? t('login.signingIn') : t('login.signInButton')}
                </button>
              </form>
            ) : (
              <form className={styles.authForm} onSubmit={handleSignup}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('login.email')}</label>
                  <input
                    type="email"
                    className={styles.formInput}
                    placeholder={t('login.emailPlaceholder')}
                    value={signupData.email}
                    onChange={(e) => setSignupData(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('login.password')}</label>
                  <div className={styles.passwordContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className={styles.formInput}
                      placeholder={t('login.createPassword')}
                      value={signupData.password}
                      onChange={(e) => setSignupData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('login.confirmPassword')}</label>
                  <input
                    type="password"
                    className={styles.formInput}
                    placeholder={t('login.confirmPasswordPlaceholder')}
                    value={signupData.confirmPassword}
                    onChange={(e) => setSignupData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('login.videoTypeLabel')}
                    <span style={{ color: '#e74c3c', marginLeft: '2px' }}>{t('login.videoTypeRequired')}</span>
                  </label>
                  <select
                    className={styles.formInput}
                    value={signupData.videoType}
                    onChange={(e) => setSignupData(prev => ({ 
                      ...prev, 
                      videoType: e.target.value,
                      videoTypeOther: e.target.value !== 'other' ? '' : prev.videoTypeOther
                    }))}
                    required
                  >
                    <option value="">{t('login.videoTypePlaceholder')}</option>
                    <option value="education">{t('login.videoTypeEducation')}</option>
                    <option value="marketing">{t('login.videoTypeMarketing')}</option>
                    <option value="devotional">{t('login.videoTypeDevotional')}</option>
                    <option value="content-creation">{t('login.videoTypeContentCreation')}</option>
                    <option value="cinematic">{t('login.videoTypeCinematic')}</option>
                    <option value="other">{t('login.videoTypeOther')}</option>
                  </select>
                  
                  {signupData.videoType === 'other' && (
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder={t('login.videoTypeOtherPlaceholder')}
                      value={signupData.videoTypeOther}
                      onChange={(e) => setSignupData(prev => ({ 
                        ...prev, 
                        videoTypeOther: e.target.value 
                      }))}
                      required
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </div>

                <CaptchaBox onVerify={handleCaptchaChange} />

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isLoading}
                >
                  {isLoading ? t('login.creatingAccount') : t('login.createAccountButton')}
                </button>
              </form>
            )}

            {error && (
              <div className={styles.errorMessage}>
                {error}
                {isUnverifiedError && activeTab === 'login' && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResendingVerification}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#4a9eff',
                        textDecoration: 'underline',
                        cursor: isResendingVerification ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        padding: '0',
                        opacity: isResendingVerification ? 0.6 : 1
                      }}
                    >
                      {isResendingVerification 
                        ? t('login.resending', 'Resending...') 
                        : t('login.resendVerificationEmail', "Didn't receive the email? Resend verification email")}
                    </button>
                  </div>
                )}
              </div>
            )}
            {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
          </div>
        </div>

        {/* Right Section - Benefits & Features */}
        <div className={styles.rightSection}>
          <div className={styles.rightContent}>
            <h2 className={styles.sectionTitle}>{t('login.whyChoose')}</h2>
            <p className={styles.sectionDescription}>
              {t('login.whyChooseDescription')}
            </p>

            <div className={styles.benefitsList}>
              {benefits.map((benefit, index) => (
                <div key={index} className={styles.benefitItem}>
                  <div className={styles.benefitIcon}>{benefit.icon}</div>
                  <div className={styles.benefitContent}>
                    <h3>{benefit.title}</h3>
                    <p>{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* <h3 className={styles.featuresTitle}>{t('login.featuresTitle')}</h3>
            <div className={styles.featuresGrid}>
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className={`${styles.featureCard} ${
                    index === currentFeature ? styles.featured : ''
                  }`}
                >
                  <div className={styles.featureIcon}>{feature.icon}</div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div> */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
